// src/pages/AssumeUserPage.tsx
//
// ★★ ADR 0015 — AFTER SSO, CHOOSE WHO TO ACT AS. A System Administrator's landing page, and what the Switch user button returns to.
//
// Pick a demonstration account and continue: from then on the app behaves exactly as that account would, with that account's scope.
// Nothing on screen announces it afterwards, by decision, so the Switch user button in the header is how an administrator gets back
// here. "Manage users as administrator" is the way back to the administrator's own account, which is the only one that can reach
// User accounts.
//
// ⚠ The list is the server's, and what is shown is its own label, verbatim. The server checks the choice again when it is made, so a
// stale list cannot be used to act as an account that is no longer a demonstration account.
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { SgdsButton, SgdsSelect, SgdsSelectOption } from '@govtechsg/sgds-web-component/react'
import { api, ApiError, isNetworkError } from '../lib/api'
import { useApi } from '../lib/useApi'
import { useAuth } from '../state/AuthContext'
import { targetValue } from '../lib/sgds-dom'
import { useDocumentTitle } from '../lib/crumbs'
import { ErrorCard, LoadingCard } from '../components/StateCards'
import { FormResult, type FormResultState } from '../components/FormResult'

const NOTHING = ''

export function AssumeUserPage() {
  useDocumentTitle('Switch user')
  const { assumeUser, assumeSelf } = useAuth()
  const navigate = useNavigate()
  const list = useApi(() => api.assumeAccounts(), [])
  const [chosen, setChosen] = useState(NOTHING)
  const [state, setState] = useState<FormResultState>({ status: 'idle' })

  const run = async (act: () => Promise<void>, to: string) => {
    setState({ status: 'submitting' })
    try {
      await act()
      navigate(to, { replace: true })
    } catch (error) {
      setState({
        status: 'error',
        message: error instanceof ApiError ? error.message : isNetworkError(error) ? 'PRS could not be reached. Try again.' : 'Could not switch user.',
      })
    }
  }

  const busy = state.status === 'submitting'
  const accounts = list.data?.accounts ?? []

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-3xl mb-2">Switch user</h1>
        <p className="text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
          Choose which user to act as. You will see PRS exactly as that user does, with the same access and no more. Use Switch user in the
          header to come back here.
        </p>
      </div>

      {list.error !== null ? (
        <ErrorCard title="Could not load the users" message={list.error} onRetry={list.reload} />
      ) : list.initialLoading ? (
        <LoadingCard label="Loading users" rows={3} />
      ) : (
        <div className="pt-card flex flex-col gap-4">
          <SgdsSelect
            label="Act as"
            placeholder="Choose a user"
            value={chosen}
            disabled={busy}
            onSgdsChange={(e) => {
              setChosen(targetValue(e))
              setState({ status: 'idle' })
            }}
          >
            <SgdsSelectOption value={NOTHING}>Choose a user&hellip;</SgdsSelectOption>
            {accounts.map((a) => (
              <SgdsSelectOption key={a.userId} value={a.userId}>
                {a.label}
              </SgdsSelectOption>
            ))}
          </SgdsSelect>
          {accounts.length === 0 && (
            <p className="text-sm text-[var(--sgds-body-color-subtle)] m-0">There are no users to act as. Add one from User accounts.</p>
          )}
          {list.data?.truncated === true && (
            <p className="text-sm text-[var(--sgds-body-color-subtle)] m-0">Only the first part of the list is shown.</p>
          )}
          <div>
            <SgdsButton
              variant="primary"
              type="button"
              loading={busy}
              disabled={busy || chosen === NOTHING}
              onClick={() => void run(() => assumeUser(chosen), '/dashboard')}
            >
              Continue as this user
            </SgdsButton>
          </div>
          <FormResult state={state} />
        </div>
      )}

      {list.data !== undefined && (
        <div className="pt-card flex flex-col gap-3">
          <h2 className="text-xl">Manage users as administrator</h2>
          <p className="text-sm text-[var(--sgds-body-color-subtle)] max-w-[70ch] m-0">
            Go back to your own account, {list.data.administrator.displayName}, to add people and change their roles. You will not see any
            bills there.
          </p>
          <div>
            <SgdsButton variant="outline" type="button" disabled={busy} onClick={() => void run(() => assumeSelf(), '/administration/users')}>
              Manage users as administrator
            </SgdsButton>
          </div>
        </div>
      )}
    </div>
  )
}
